import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { setFavorite } from '@/api/customer';
import useThrottle from '@/hooks/throttle';
import useNeedLogin from '@/hooks/useNeedLogin';
import useAccount from '@/store/account';

import styles from './index.module.scss';
import { formatImpression, sendEvent } from '@weee-fe/weee-biz-web';
import { NewTracking } from '@/constants/track';

export interface SlodOutProps {
  className?: string;
  id?: number;
  onClick?: () => void;
  isLogin?: boolean;
  isFavorite?: boolean;
  onSuccess?: (id: number | string, isFavorite?: boolean, next?: () => void) => void;
  render?: (
    node?: boolean,
    toggleCollect?: (e?: React.MouseEvent<HTMLElement>) => void,
    dom?: React.ReactNode
  ) => React.ReactNode;
  clickTracking?: () => void;
  data?: any;
}

function SoldOut({ className, id, onSuccess, onClick, isFavorite, render, clickTracking, data = {} }: SlodOutProps) {
  const { t } = useTranslation('common');
  const [reminded, setReminded] = useState<boolean>(isFavorite || false);
  const { checkIsLogin } = useNeedLogin();

  const toggleCollect = useThrottle(() => {
    onClick?.();
    if (onSuccess) {
      return onSuccess?.(id, !isFavorite, () => {
        setReminded(reminded => !reminded);
      });
    }

    setReminded(reminded => !reminded);
  }, 500);

  const remindMe = useThrottle(e => {
    e.stopPropagation();
    e.preventDefault();
    sendEvent({
      type: NewTracking.clickAction,
      params: {
        co: {
          ...(formatImpression.formatProductInfo(data, false) as any),
          target_nm: id,
          target_pos: 0,
          target_type: 'product_notify_me',
          click_type: reminded ? 'cancel_selection' : 'normal'
        }
      }
    });
    if (!checkIsLogin()) return;
    clickTracking?.();
    toggleCollect();
  }, 500);

  const defaultNode = (
    <button
      // inline={true}
      onClick={remindMe}
      data-role="unAvailableButton"
      className={classNames(styles.container, reminded ? styles.soldOutReminded : styles.soldOut, className)}>
      {reminded ? t('sold_out_reminded') : t('sold_out_remind')}
    </button>
  );
  useEffect(() => {
    setReminded(isFavorite);
  }, [isFavorite]);
  if (render) {
    return render(reminded, toggleCollect, defaultNode) as ReactElement<any>;
  }
  return defaultNode;
}

const SoldOutWrapper = ({ id, onClick, render, ...rest }: SlodOutProps) => {
  const { favoriteIds = [], syncFavoriteIds } = useAccount();
  const isFavorite = useMemo(() => {
    return favoriteIds?.includes(+id);
  }, [id, favoriteIds?.join('_')]);
  const onSuccess = (id, reminded, next) => {
    setFavorite({
      type: 'product_favorite_a',
      target_id: id
    })
      .then(res => {
        next();
        let newIds = [...favoriteIds];
        if (reminded) {
          newIds = newIds.concat(id);
        } else {
          newIds = favoriteIds.filter(item => item !== +id);
        }

        syncFavoriteIds(newIds);
      })
      .catch();
  };

  return (
    <SoldOut
      id={id}
      onClick={onClick}
      isFavorite={isFavorite}
      {...rest}
      onSuccess={onSuccess}
      render={(id, toggle, defaultNode) => {
        const node = render ? render(id, toggle, defaultNode) : defaultNode;
        return node;
      }}
    />
  );
};

export default SoldOutWrapper;
